import React from 'react';
import { useIntl } from 'react-intl';

import { Button, Dialog, IconButton } from '@bolteu/clay-react';
import { ArrowCircleDown, ArrowCircleUp, Cross, Flag } from '@bolteu/kalep-react-icons';

import { $tk } from 'utils/appTranslationKey';

import illustrationSrc from './assets/bolt-star-page-onboarding-illustration.jpg';

const dialogTk = $tk.widgets.boltStarPageOnboardingDialog;

const BULLETS = [
  { Icon: Flag, labelKey: dialogTk.bullets.metrics },
  { Icon: ArrowCircleUp, labelKey: dialogTk.bullets.levelUp },
  { Icon: ArrowCircleDown, labelKey: dialogTk.bullets.lowerLevels },
];

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export interface BoltStarPageOnboardingDialogProps {
  isOpen: boolean;
  /** Fired only by the close button: the backdrop and Escape deliberately do not dismiss. */
  onClose: () => void;
  onAction: () => void;
}

export function BoltStarPageOnboardingDialog({
  isOpen,
  onClose,
  onAction,
}: BoltStarPageOnboardingDialogProps) {
  const intl = useIntl();

  const handleOpenChange = (open: boolean, eventDetails: DialogChangeEventDetails) => {
    // block closing via shroud or ESC
    if (!open && eventDetails.reason === 'close-press') {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-110 shadow-lg">
          <Dialog.Close
            render={
              <IconButton
                aria-label={intl.formatMessage({
                  id: $tk.widgets.boltStarOnboardingDialog.closeLabel,
                })}
                icon={<Cross />}
                size="sm"
                variant="inverted"
                className="bg-active-neutral-secondary-hard hover:bg-active-neutral-secondary-hard"
              />
            }
            className="absolute right-3 top-2 z-10"
          />
          {/* The whole body scrolls on short viewports instead of squeezing the list, while the
              button stays pinned. */}
          <div className="bolt-font-body-m-regular grow overflow-y-auto overscroll-none text-primary">
            <img
              src={illustrationSrc}
              alt={intl.formatMessage({ id: dialogTk.illustrationAlt })}
              width={440}
              height={280}
              className="block h-auto w-full"
            />
            <Dialog.Title
              className="bolt-font-heading-m-accent m-0 px-6 pb-3 pt-4"
              // eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component
              render={<h2 />}
            >
              {intl.formatMessage({ id: dialogTk.title })}
            </Dialog.Title>
            <Dialog.Description className="m-0 list-none overflow-visible p-0" render={<ul />}>
              {BULLETS.map(({ Icon, labelKey }) => (
                <li key={labelKey} className="flex items-start gap-4 px-6 py-3">
                  <Icon aria-hidden className="shrink-0 text-action-primary" />
                  {intl.formatMessage({ id: labelKey })}
                </li>
              ))}
            </Dialog.Description>
          </div>
          <Dialog.Footer className="shrink-0">
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {intl.formatMessage({ id: dialogTk.actionLabel })}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
